import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { trackTimeOnStep, trackTutorialBegin } from "@/utils/analytics";
import { updateJourneyStage } from "@/utils/journeyTracking";
import { CreatePageState, CreatePageAction } from "@/stores/createPageStore";
import { useCreditsContext } from "@/context/providers/CreditsProvider";

interface UseGenerationLifecycleProps {
  state: CreatePageState;
  dispatch: React.Dispatch<CreatePageAction>;
  session: any;
  roles: { admin?: boolean };
  trackCreateStep: (step: number) => void;
}

export function useGenerationLifecycle({
  state,
  dispatch,
  session,
  roles,
  trackCreateStep,
}: UseGenerationLifecycleProps) {
  const { refresh: invalidateCredits } = useCreditsContext();
  const searchParams = useSearchParams();

  // Extract stable param values to check if returning from purchase
  const creditsPurchasedParam = searchParams.get("credits_purchased");
  const restoreFormParam = searchParams.get("restore_form");
  const isReturningFromPurchase =
    creditsPurchasedParam === "true" || restoreFormParam === "true";

  // Progress tracking refs
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const wasProcessingRef = useRef(false);
  const progressRef = useRef(0);
  const generationStartTimeRef = useRef<number | null>(null);
  const isModifyingRef = useRef(false);
  const isUploadRef = useRef(false);
  const generationPhaseRef = useRef<"idle" | "front" | "back" | "complete">(
    "idle"
  );

  // Step tracking refs
  const stepStartTimeRef = useRef<Record<number, number>>({});
  const lastStepRef = useRef(state.form.currentStep);
  const lastCreditsRefreshedStepRef = useRef<number | null>(null);

  // Analytics tracking refs
  const lastTrackedStepRef = useRef<number | null>(null);

  // Progress Tracking Effect
  useEffect(() => {
    // Update refs with latest state values to avoid stale closures
    generationStartTimeRef.current = state.generation.progress.startTime;
    isModifyingRef.current = state.ui.modification.isModifying;
    generationPhaseRef.current = state.generation.progress.phase;

    // Determine if current generation is using an uploaded image
    // Check based on phase (front vs back) and form data
    const isUpload =
      state.form.data.isUploadPreset ||
      (state.generation.progress.phase === "front" &&
        !!(
          state.form.data.uploadedFrontImages &&
          state.form.data.uploadedFrontImages.length > 0
        )) ||
      (state.generation.progress.phase === "back" &&
        !!(
          state.form.data.uploadedBackImages &&
          state.form.data.uploadedBackImages.length > 0
        ));
    isUploadRef.current = isUpload;

    const isProcessing =
      state.generation.progress.loading || state.ui.modification.isModifying;

    if (isProcessing && !wasProcessingRef.current) {
      // Transition: idle -> processing
      wasProcessingRef.current = true;
      progressRef.current = 0;
      dispatch({ type: "SET_PROGRESS", payload: 0 });

      progressIntervalRef.current = setInterval(() => {
        const cap = 91;
        // Smoother progress: increment by 2-3% per second instead of random jumps
        const increment = 2 + Math.random();
        progressRef.current = Math.min(progressRef.current + increment, cap);
        dispatch({ type: "SET_PROGRESS", payload: progressRef.current });

        // Use refs to get latest values instead of stale closure values
        const startTime = generationStartTimeRef.current;
        if (startTime) {
          const elapsed = (Date.now() - startTime) / 1000;
          // All generations use gemini-3-pro-image-preview (45s ETA)
          const totalEstimated = 45;
          const remaining = Math.max(0, totalEstimated - elapsed);
          dispatch({
            type: "SET_GENERATION_TIMING",
            payload: {
              startTime: startTime,
              estimatedTime: remaining,
            },
          });
        }
      }, 1000);
    } else if (!isProcessing && wasProcessingRef.current) {
      // Transition: processing -> idle
      wasProcessingRef.current = false;
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
      dispatch({ type: "SET_PROGRESS", payload: 100 });
      dispatch({
        type: "SET_GENERATION_TIMING",
        payload: { startTime: null, estimatedTime: null },
      });
      setTimeout(() => dispatch({ type: "SET_PROGRESS", payload: 0 }), 800);
    }

    return () => {
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
    };
  }, [
    state.generation.progress.loading,
    state.ui.modification.isModifying,
    state.generation.progress.startTime,
    state.generation.progress.phase,
    state.form.data.isUploadPreset,
    state.form.data.uploadedFrontImages,
    state.form.data.uploadedBackImages,
    dispatch,
  ]);

  // Step Tracking Effect
  useEffect(() => {
    const currentStep = state.form.currentStep;
    const previousStep = lastStepRef.current;

    // Track time spent on previous step if it changed
    if (
      previousStep !== currentStep &&
      stepStartTimeRef.current[previousStep]
    ) {
      const timeSpent = Math.round(
        (Date.now() - stepStartTimeRef.current[previousStep]) / 1000
      );
      if (timeSpent > 0) {
        trackTimeOnStep({
          step_index: previousStep,
          time_seconds: timeSpent,
        });
      }
    }

    // Update journey stage when step changes
    if (currentStep !== previousStep) {
      updateJourneyStage("design", currentStep);
    }

    // Record start time for current step
    if (!stepStartTimeRef.current[currentStep]) {
      stepStartTimeRef.current[currentStep] = Date.now();
    }

    lastStepRef.current = currentStep;
  }, [state.form.currentStep]);

  // Refresh credits on step changes (only once per step)
  // Skip refresh when returning from purchase to prevent spam
  useEffect(() => {
    if (!session || roles.admin) return;

    // Skip credit refresh if returning from purchase - useCreditManagement handles it
    if (isReturningFromPurchase) {
      return;
    }

    if (state.form.currentStep !== lastCreditsRefreshedStepRef.current) {
      // Fire and forget - credits will update in the background
      invalidateCredits().catch(() => {
        // Silently fail - this is a background refresh
      });
      lastCreditsRefreshedStepRef.current = state.form.currentStep;
    }
  }, [
    state.form.currentStep,
    session,
    roles.admin,
    invalidateCredits,
    isReturningFromPurchase,
  ]);

  // Analytics Tracking Effects
  // Funnel: fire tutorial_begin once per session when on create flow
  useEffect(() => {
    try {
      const key = "ga_tutorial_begin_fired";
      if (typeof window !== "undefined" && window.sessionStorage) {
        if (!sessionStorage.getItem(key)) {
          trackTutorialBegin();
          sessionStorage.setItem(key, "1");
        }
      }
    } catch {
      // ignore
    }
    // only on mount
  }, []);

  // Funnel: track step progress whenever currentStep changes
  useEffect(() => {
    if (
      state.form.currentStep &&
      state.form.currentStep !== lastTrackedStepRef.current
    ) {
      lastTrackedStepRef.current = state.form.currentStep;
      trackCreateStep(state.form.currentStep);
    }
  }, [state.form.currentStep, trackCreateStep]);
}
